import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Custom Tour Engine — replaces react-joyride entirely
//
// Features:
//   - SVG spotlight cutout around target element
//   - Auto-positioned tooltip (right→left→below→above fallback)
//   - Special welcome modal for steps with isWelcome:true
//   - skipIfNotFound: automatically advances when target doesn't exist in DOM
//   - onSidebarReady: called when tour starts so host can expand sidebar
//   - targets: optional string[] — first visible + hit-tested selector wins (see resolveTourTargetElement)
//   - Overlay click does NOT dismiss tour (intentional — prevents accidental close)
// ─────────────────────────────────────────────────────────────────────────────

const OVERLAY_Z = 99990;
const TOOLTIP_Z = 99995;
const SPOTLIGHT_PADDING = 6;
const TOOLTIP_GAP = 14;

// ── DOM helpers ──────────────────────────────────────────────────────────────
function resolveTourTargetElement(step) {
  const selectors = Array.isArray(step?.targets) && step.targets.length > 0
    ? step.targets
    : step?.target
      ? [step.target]
      : [];
  for (const sel of selectors) {
    let el;
    try {
      el = document.querySelector(sel);
    } catch {
      el = null;
    }
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const px = Math.min(Math.max(cx, rect.left + 2), rect.right - 2);
    const py = Math.min(Math.max(cy, rect.top + 2), rect.bottom - 2);
    let top = null;
    try {
      top = document.elementFromPoint(px, py);
    } catch {
      top = null;
    }
    if (top && (el === top || el.contains(top))) return el;
  }
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return el;
  }
  return null;
}

function getElementRectForStep(step) {
  if (!step?.target && !(Array.isArray(step?.targets) && step.targets.length)) return null;
  const el = resolveTourTargetElement(step);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.height === 0 || rect.width === 0) return null;
  return {
    top: rect.top, left: rect.left,
    width: rect.width, height: rect.height,
    bottom: rect.bottom, right: rect.right,
  };
}

function stepTargetLabel(step) {
  if (Array.isArray(step?.targets) && step.targets.length) return step.targets.join(' | ');
  return step?.target ?? '';
}

// Returns {top, left} in px strings, OR {centered:true} for null target
function computeTooltipPosition(targetRect, tooltipRef) {
  if (!targetRect || !tooltipRef.current) return { centered: true };

  const tooltip = tooltipRef.current.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const rightSpace = vw - targetRect.right;
  const leftSpace = targetRect.left;
  const bottomSpace = vh - targetRect.bottom;

  let top, left;

  if (rightSpace >= tooltip.width + TOOLTIP_GAP + 20) {
    left = targetRect.right + TOOLTIP_GAP;
    top = targetRect.top + targetRect.height / 2 - tooltip.height / 2;
  } else if (leftSpace >= tooltip.width + TOOLTIP_GAP + 20) {
    left = targetRect.left - tooltip.width - TOOLTIP_GAP;
    top = targetRect.top + targetRect.height / 2 - tooltip.height / 2;
  } else if (bottomSpace >= tooltip.height + TOOLTIP_GAP + 20) {
    left = targetRect.left + targetRect.width / 2 - tooltip.width / 2;
    top = targetRect.bottom + TOOLTIP_GAP;
  } else {
    left = targetRect.left + targetRect.width / 2 - tooltip.width / 2;
    top = targetRect.top - tooltip.height - TOOLTIP_GAP;
  }

  // Clamp to viewport
  top = Math.max(8, Math.min(vh - tooltip.height - 8, top));
  left = Math.max(8, Math.min(vw - tooltip.width - 8, left));

  return { top: `${top}px`, left: `${left}px` };
}

// ─────────────────────────────────────────────────────────────────────────────
// Spotlight Overlay — SVG mask creates crisp cutout
// NOTE: onClick intentionally removed — clicking outside does NOT skip the tour
// ─────────────────────────────────────────────────────────────────────────────
const SpotlightOverlay = ({ targetRect, step }) => {
  if (!targetRect) {
    // No target = full dim overlay (for centered/welcome modal)
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: OVERLAY_Z,
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        transition: 'opacity 200ms ease',
      }} />
    );
  }

  const pad = SPOTLIGHT_PADDING;
  // If the step requires user action, we must let pointer events pass through the overlay
  // so they can actually click the element!
  const pointerEvents = step?.waitForAction ? 'none' : 'auto';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: OVERLAY_Z, pointerEvents }}>
      {/* SVG mask for spotlight cutout */}
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - pad}
              y={targetRect.top - pad}
              width={targetRect.width + pad * 2}
              height={targetRect.height + pad * 2}
              rx="8" ry="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.15)" mask="url(#tour-spotlight-mask)" pointerEvents="none" />
      </svg>
      {/* Spotlight ring glow */}
      {/* keyframes injected once by TourEngine useEffect — no inline style needed */}
      <div style={{
        position: 'absolute',
        top: targetRect.top - pad,
        left: targetRect.left - pad,
        width: targetRect.width + pad * 2,
        height: targetRect.height + pad * 2,
        borderRadius: 8,
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.5), 0 0 0 5px rgba(59, 130, 246, 0.2)',
        pointerEvents: 'none',
        transition: 'all 300ms ease',
        animation: step?.waitForAction ? 'tourPulseRingBlue 1.5s infinite' : 'none',
      }} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Button styles
// ─────────────────────────────────────────────────────────────────────────────
function btnStyle(variant, isDark) {
  if (variant === 'primary') {
    return {
      padding: '7px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
      fontSize: 13, fontWeight: 600,
      backgroundColor: '#2563eb', color: '#fff',
      transition: 'background-color 150ms',
    };
  }
  return {
    padding: '7px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 500, backgroundColor: 'transparent',
    color: isDark ? '#9ca3af' : '#6b7280',
    transition: 'color 150ms',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip Card
// ─────────────────────────────────────────────────────────────────────────────
const TooltipCard = React.forwardRef(({ step, index, total, onNext, onBack, onSkip }, ref) => {
  const isDark = document.documentElement.classList.contains('dark');
  const isLast = index === total - 1;
  const isWelcome = step?.isWelcome === true;
  const progress = total > 1 ? (index / (total - 1)) * 100 : 100;

  // ── Welcome Step ──────────────────────────────────────────────────────────
  if (isWelcome) {
    // Two-column layout when a custom hero image is provided
    if (step.image) {
      return (
        <div
          ref={ref}
          style={{
            display: 'flex',
            width: 740, maxWidth: 'calc(100vw - 40px)',
            minHeight: 340,
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: isDark
              ? '0 32px 64px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.07)'
              : '0 24px 56px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
            fontFamily: 'Inter, system-ui, sans-serif',
            backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
            animation: 'tourWelcomeIn 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}
        >
          {/* Left — hero image (40 % of card width) */}
          <div style={{ width: 320, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
            <img
              src={step.image}
              alt=""
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center 35%',
              }}
            />
            {/* Right-edge feather to blend into content pane */}
            <div style={{
              position: 'absolute', inset: 0,
              background: isDark
                ? 'linear-gradient(to right, transparent 50%, rgba(26,31,46,0.6) 100%)'
                : 'linear-gradient(to right, transparent 50%, rgba(255,255,255,0.45) 100%)',
              pointerEvents: 'none',
            }} />
          </div>

          {/* Right — content (60 % of card width) */}
          <div style={{
            flex: 1,
            display: 'flex', flexDirection: 'column',
            padding: '28px 28px 24px',
          }}>

            {/* ── Top section — grows to fill available space ── */}
            <div style={{ flex: 1 }}>
              <span style={{
                display: 'block',
                fontSize: 10, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: '#6366f1',
                marginBottom: 6,
              }}>
                Guided Tour
              </span>

              <h2 style={{
                margin: '0 0 12px',
                fontSize: 20, fontWeight: 800, lineHeight: 1.3,
                letterSpacing: '-0.025em',
                color: isDark ? '#f1f5f9' : '#0f172a',
              }}>
                {step.title}
              </h2>

              <p style={{
                margin: 0,
                fontSize: 13.5, lineHeight: 1.65,
                color: isDark ? '#94a3b8' : '#64748b',
              }}>
                {step.content}
              </p>
            </div>

            {/* ── Divider ── */}
            <div style={{
              height: 1,
              backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0',
              margin: '20px 0 16px',
            }} />

            {/* ── Footer ── */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{
                fontSize: 11, fontWeight: 500,
                color: isDark ? '#475569' : '#94a3b8',
              }}>
                {total - 1} steps
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={onSkip}
                  style={{
                    padding: '7px 12px', border: 'none', borderRadius: 8,
                    background: 'transparent', cursor: 'pointer',
                    fontSize: 12, fontWeight: 500,
                    color: isDark ? '#475569' : '#94a3b8',
                  }}
                >
                  Skip
                </button>
                <button
                  onClick={onNext}
                  style={{
                    padding: '8px 20px', border: 'none', borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13, fontWeight: 600,
                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(99,102,241,0.38)',
                    transition: 'transform 140ms ease, box-shadow 140ms ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(99,102,241,0.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.38)'; }}
                >
                  {isLast ? 'Finish' : 'Start Tour →'}
                </button>
              </div>
            </div>

          </div>
        </div>
      );
    }

    // Fallback: full-width gradient hero (no custom image)
    return (
      <div
        ref={ref}
        style={{
          width: 500, maxWidth: 'calc(100vw - 40px)',
          borderRadius: 22,
          overflow: 'hidden',
          boxShadow: '0 40px 80px -16px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.08)',
          fontFamily: 'Inter, system-ui, sans-serif',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.6)',
          backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
          color: isDark ? '#f9fafb' : '#111827',
          animation: 'tourWelcomeIn 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}
      >
        {/* Gradient hero header with shimmer + floating particles */}
        <div style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #1e40af 0%, #4f46e5 45%, #7c3aed 100%)',
          padding: '40px 32px 32px',
          textAlign: 'center',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0, opacity: 0.35,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            backgroundSize: '200% 100%',
            animation: 'tourShimmer 2.4s linear infinite',
            pointerEvents: 'none',
          }} />
          {[
            { top: '18%', left: '10%', size: 10, anim: 'tourFloat1 3.2s ease-in-out infinite', delay: '0s', color: 'rgba(165,180,252,0.7)' },
            { top: '55%', left: '6%',  size:  7, anim: 'tourFloat2 2.8s ease-in-out infinite', delay: '0.6s', color: 'rgba(196,181,253,0.6)' },
            { top: '25%', right: '8%', size: 12, anim: 'tourFloat1 3.6s ease-in-out infinite', delay: '0.3s', color: 'rgba(147,197,253,0.65)' },
            { top: '62%', right: '5%', size:  8, anim: 'tourFloat3 2.5s ease-in-out infinite', delay: '0.9s', color: 'rgba(216,180,254,0.6)' },
            { top: '78%', left: '18%', size:  6, anim: 'tourFloat2 3.0s ease-in-out infinite', delay: '1.1s', color: 'rgba(165,243,252,0.5)' },
          ].map((d, i) => (
            <div key={i} style={{
              position: 'absolute', top: d.top, left: d.left, right: d.right,
              width: d.size, height: d.size, borderRadius: '50%',
              backgroundColor: d.color, animation: d.anim, animationDelay: d.delay,
              pointerEvents: 'none',
            }} />
          ))}
          <div style={{
            fontSize: 58, marginBottom: 16, lineHeight: 1,
            display: 'inline-block',
            animation: 'tourWave 1.8s ease-in-out 1',
            transformOrigin: '70% 70%', position: 'relative', zIndex: 1,
          }}>👋</div>
          <h2 style={{
            margin: 0, fontSize: 24, fontWeight: 800,
            color: '#ffffff', lineHeight: 1.3, letterSpacing: '-0.03em',
            textShadow: '0 2px 12px rgba(0,0,0,0.25)', position: 'relative', zIndex: 1,
          }}>
            {step.title}
          </h2>
        </div>

        <div style={{
          padding: '24px 32px 18px',
          fontSize: 14.5, lineHeight: 1.8,
          color: isDark ? '#cbd5e1' : '#475569',
          textAlign: 'center', whiteSpace: 'pre-line',
        }}>
          {step.content}
        </div>

        <div style={{
          padding: '16px 32px 24px',
          borderTop: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
        }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
            {total - 1} steps ahead
          </span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={onSkip} style={{ ...btnStyle('text', isDark), color: isDark ? '#64748b' : '#94a3b8', fontSize: 13 }}>
              Skip Tour
            </button>
            <button
              onClick={onNext}
              style={{
                ...btnStyle('primary', isDark),
                padding: '10px 28px', fontSize: 14,
                background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                boxShadow: '0 4px 14px rgba(99,102,241,0.45)', borderRadius: 8,
                transition: 'transform 150ms, box-shadow 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.55)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 14px rgba(99,102,241,0.45)'; }}
            >
              {isLast ? 'Finish' : 'Start Tour →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal Step: directional slide-in card ────────────────────────────────
  return (
    <div
      ref={ref}
      style={{
        width: 360, maxWidth: 'calc(100vw - 32px)',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: isDark
          ? '0 24px 48px -8px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)'
          : '0 24px 48px -8px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
        fontFamily: 'Inter, system-ui, sans-serif',
        backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
        color: isDark ? '#f1f5f9' : '#0f172a',
        animation: 'tourSlideIn 240ms cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      {/* Gradient accent bar top */}
      <div style={{
        height: 3,
        background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
      }} />

      {/* Header */}
      <div style={{
        padding: '14px 18px 12px',
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9',
        background: isDark ? 'rgba(255,255,255,0.03)' : '#f8faff',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        {/* Step badge */}
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%',
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          color: '#fff', fontSize: 11, fontWeight: 700,
          flexShrink: 0,
        }}>{index}</span>
        <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em', flex: 1 }}>
          {step.title}
        </h3>
      </div>

      {/* Body */}
      <div style={{
        padding: '13px 18px 11px',
        fontSize: 13, lineHeight: 1.7,
        color: isDark ? '#94a3b8' : '#475569',
        whiteSpace: 'pre-line',
      }}>
        {step.content}
      </div>

      {/* Optional inline action button (e.g. "Try Sample Data") */}
      {step.footerButton && (
        <div style={{ padding: '0 18px 12px' }}>
          <button
            onClick={step.footerButton.onClick}
            style={{
              width: '100%',
              padding: '9px 16px',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.01em',
              boxShadow: '0 3px 10px rgba(14,165,233,0.4)',
              transition: 'transform 120ms, box-shadow 120ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 5px 16px rgba(14,165,233,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 3px 10px rgba(14,165,233,0.4)'; }}
          >
            {step.footerButton.label}
          </button>
        </div>
      )}

      {/* Progress bar */}
      <div style={{
        height: 2,
        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0',
        margin: '0 18px',
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
          borderRadius: 2,
          transition: 'width 350ms ease',
        }} />
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 18px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#475569' : '#94a3b8' }}>
          {index} / {total - 1}
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {!isLast && (
            <button onClick={onSkip} style={{ ...btnStyle('text', isDark), fontSize: 12 }}>Skip</button>
          )}
          {index > 0 && !step.waitForAction && (
            <button onClick={onBack} style={{ ...btnStyle('text', isDark), fontSize: 12 }}>Back</button>
          )}
          {step.waitForAction ? (
            <span style={{
              fontSize: 12, fontWeight: 600, color: '#f59e0b',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: '#f59e0b', display: 'inline-block',
                animation: 'tourPulse 1s ease-in-out infinite',
              }} />
              Waiting…
            </span>
          ) : (
            <button
              onClick={onNext}
              style={{
                ...btnStyle('primary', isDark),
                background: isLast
                  ? 'linear-gradient(135deg, #059669, #0891b2)'
                  : 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                boxShadow: isLast
                  ? '0 3px 10px rgba(5,150,105,0.35)'
                  : '0 3px 10px rgba(99,102,241,0.35)',
                borderRadius: 7,
                fontSize: 13, padding: '7px 16px',
                transition: 'transform 120ms, box-shadow 120ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
            >
              {isLast ? '🎉 Finish' : 'Next →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// TourEngine — main export
//
// Props:
//   steps         — array of step objects
//   run           — boolean: true = tour is active
//   setRun        — setter for run state
//   markSeen      — callback when tour finishes/skips
//   onSidebarReady — optional callback invoked once when tour starts,
//                    allowing PlatformLayout to expand sidebar + sections
// ─────────────────────────────────────────────────────────────────────────────
export const TourEngine = ({ steps, run, setRun, markSeen, onSidebarReady = null, onStepChange = null }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ centered: true });
  const tooltipRef = useRef(null);
  const rafRef = useRef(null);
  const sidebarReadyFiredRef = useRef(false);
  const actionPollRef = useRef(null);

  // Inject keyframes once
  useEffect(() => {
    if (document.getElementById('tour-keyframes')) return;
    const style = document.createElement('style');
    style.id = 'tour-keyframes';
    style.textContent = `
      /* ── Welcome modal: spring-physics entrance ── */
      @keyframes tourWelcomeIn {
        0%   { opacity: 0; transform: scale(0.82) translateY(28px); }
        60%  { opacity: 1; transform: scale(1.03) translateY(-4px); }
        80%  { transform: scale(0.98) translateY(2px); }
        100% { opacity: 1; transform: scale(1) translateY(0); }
      }
      /* ── Normal tooltip: directional slide-in ── */
      @keyframes tourSlideIn {
        from { opacity: 0; transform: translateX(-14px) scale(0.95); }
        to   { opacity: 1; transform: translateX(0)    scale(1); }
      }
      /* ── Waiting pulse dot ── */
      @keyframes tourPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%      { opacity: 0.3; transform: scale(0.6); }
      }
      /* ── Gradient shimmer on welcome hero ── */
      @keyframes tourShimmer {
        0%   { background-position: -200% center; }
        100% { background-position:  200% center; }
      }
      /* ── Floating particle dots ── */
      @keyframes tourFloat1 {
        0%, 100% { transform: translateY(0px)   scale(1);    opacity: 0.55; }
        50%       { transform: translateY(-14px) scale(1.18); opacity: 0.9;  }
      }
      @keyframes tourFloat2 {
        0%, 100% { transform: translateY(0px)   scale(1);    opacity: 0.4;  }
        50%       { transform: translateY(-10px) scale(1.12); opacity: 0.75; }
      }
      @keyframes tourFloat3 {
        0%, 100% { transform: translateY(0px)  scale(1);    opacity: 0.35; }
        50%       { transform: translateY(-8px) scale(1.08); opacity: 0.65; }
      }
      /* ── Waving hand ── */
      @keyframes tourWave {
        0%,  60%, 100% { transform: rotate(0deg);   }
        10%, 30%        { transform: rotate(-14deg); }
        20%, 40%        { transform: rotate(14deg);  }
      }
      /* ── Progress bar fill ── */
      @keyframes tourBarGrow {
        from { transform: scaleX(0); }
        to   { transform: scaleX(1); }
      }
      /* ── Spotlight ring pulse ── */
      @keyframes tourPulseRing {
        0%   { box-shadow: 0 0 0 0px  rgba(239,68,68,0.8), 0 0 0 3px rgba(239,68,68,0.5); }
        70%  { box-shadow: 0 0 0 15px rgba(239,68,68,0),   0 0 0 3px rgba(239,68,68,0.5); }
        100% { box-shadow: 0 0 0 0    rgba(239,68,68,0),   0 0 0 3px rgba(239,68,68,0.5); }
      }
      @keyframes tourPulseRingBlue {
        0%   { box-shadow: 0 0 0 0px  rgba(59,130,246,0.8), 0 0 0 3px rgba(59,130,246,0.5), 0 0 0 5px rgba(59,130,246,0.2); }
        70%  { box-shadow: 0 0 0 18px rgba(59,130,246,0),   0 0 0 3px rgba(59,130,246,0.5), 0 0 0 5px rgba(59,130,246,0.2); }
        100% { box-shadow: 0 0 0 0    rgba(59,130,246,0),   0 0 0 3px rgba(59,130,246,0.5), 0 0 0 5px rgba(59,130,246,0.2); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // When tour starts: reset index and call onSidebarReady once
  useEffect(() => {
    if (run) {
      console.log(`[TourEngine] 🚀 Tour started — ${steps?.length ?? 0} steps`);
      setCurrentIndex(0);
      if (onSidebarReady && !sidebarReadyFiredRef.current) {
        sidebarReadyFiredRef.current = true;
        setTimeout(() => onSidebarReady(), 400);
      }
      // Notify host about step 0
      if (onStepChange && steps?.length) {
        setTimeout(() => onStepChange(steps[0]), 450);
      }
    } else {
      sidebarReadyFiredRef.current = false;
    }
  }, [run]);

  // Poll for target element; auto-skip steps marked skipIfNotFound
  useEffect(() => {
    if (!run || !steps?.length) return;

    const step = steps[currentIndex];

    // Notify host that this step is now active (allows page navigation before DOM polling)
    if (onStepChange && currentIndex > 0) {
      onStepChange(step);
    }

    // null target(s) = centered modal (welcome step)
    if (!step?.target && !(Array.isArray(step?.targets) && step.targets.length)) {
      console.log(`[TourEngine] 👁️ Step ${currentIndex} → centered modal | title: "${step?.title}"`);
      setTargetRect(null);
      return;
    }

    console.log(`[TourEngine] 👁️ Step ${currentIndex} → target: "${stepTargetLabel(step)}" | title: "${step.title}"`);

    let attempts = 0;
    // Optional steps (skipIfNotFound) skip after ~1.5s; required steps wait up to 2s.
    // (15 × 100ms) avoids false skips right after a modal closes or a heavy re-render (e.g. dashboard tour).
    const maxAttempts = step.skipIfNotFound ? 15 : 20;

    const pollInterval = setInterval(() => {
      const el = resolveTourTargetElement(step);

      // ── Case 1: Element not in DOM yet ──────────────────────────────────
      if (!el) {
        if (++attempts >= maxAttempts) {
          clearInterval(pollInterval);
          if (step.skipIfNotFound) {
            console.log(`[TourEngine] ⏩ Target "${stepTargetLabel(step)}" not in DOM — skipIfNotFound=true, advancing`);
            setCurrentIndex(i => {
              const next = i + 1;
              if (next >= (steps?.length ?? 0)) { setRun(false); markSeen(); return i; }
              return next;
            });
          } else {
            console.warn(`[TourEngine] ⚠️ Target "${stepTargetLabel(step)}" not found — centered fallback`);
            setTargetRect(null);
          }
        }
        return;
      }

      const rect = el.getBoundingClientRect();

      // ── Case 2: Element has no visible size (inside max-h:0 accordion) ──
      // If scrollIntoView is true, the tour explicitly wants this element visible.
      // The querySelector may have returned the WRONG element (e.g. a duplicate ID
      // inside a collapsed accordion). Try querySelectorAll to find the first
      // visible match before giving up.
      if (rect.height === 0 || rect.width === 0) {
        if (step.scrollIntoView) {
          const selectorList = Array.isArray(step.targets) && step.targets.length
            ? step.targets
            : (step.target ? [step.target] : []);
          let visibleEl = null;
          let matchCount = 0;
          for (const sel of selectorList) {
            const allEls = document.querySelectorAll(sel);
            matchCount += allEls.length;
            visibleEl = Array.from(allEls).find((e) => {
              const r = e.getBoundingClientRect();
              return r.height > 0 && r.width > 0;
            });
            if (visibleEl) break;
          }
          if (visibleEl) {
            clearInterval(pollInterval);
            console.log(`[TourEngine] 🔍 Found visible alternative for "${stepTargetLabel(step)}" (${matchCount} total matches) — scrolling`);
            visibleEl.scrollIntoView({ behavior: 'instant', block: 'center' });
            let ma = 0;
            const tryM = () => {
              ma++;
              const r2 = visibleEl.getBoundingClientRect();
              if (r2.height > 0 && r2.width > 0) {
                setTargetRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height, bottom: r2.bottom, right: r2.right });
              } else if (ma < 4) {
                visibleEl.scrollIntoView({ behavior: 'instant', block: 'center' });
                setTimeout(tryM, 120);
              } else {
                setTargetRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height, bottom: r2.bottom, right: r2.right });
              }
            };
            setTimeout(tryM, 80);
            return;
          }
        }
        if (++attempts >= maxAttempts) {
          clearInterval(pollInterval);
          console.warn(`[TourEngine] ⚠️ Target "${stepTargetLabel(step)}" has zero size — centered fallback`);
          setTargetRect(null);
        }

        return;
      }

      // ── Case 3: Element found but outside visible sidebar scroll area,
      //           OR step explicitly requests scroll-into-view ────────────────
      const vh = window.innerHeight;
      const needsScroll = step.scrollIntoView || rect.top > vh || rect.bottom < 0;
      if (needsScroll) {
        clearInterval(pollInterval);
        console.log(`[TourEngine] 📜 Scrolling "${stepTargetLabel(step)}" into view (top:${Math.round(rect.top)})`);

        // Use 'instant' so the scroll position is final immediately.
        // scrollIntoView scrolls the nearest scrollable ancestor (sidebar nav),
        // not the main window, because the nav has overflow-y:auto.
        el.scrollIntoView({ behavior: 'instant', block: 'center' });

        // Re-measure up to 3 times (at 80ms intervals) to handle
        // cases where accordion CSS transition hasn't settled yet.
        let measureAttempts = 0;
        const tryMeasure = () => {
          measureAttempts++;
          const updated = el.getBoundingClientRect();
          console.log(`[TourEngine] 📍 Measure attempt ${measureAttempts}:`, {
            top: Math.round(updated.top), left: Math.round(updated.left),
            w: Math.round(updated.width), h: Math.round(updated.height),
          });
          // Valid: non-zero size AND within viewport (top > -50 and top < vh + 50)
          if (updated.height > 0 && updated.width > 0 && updated.top > -50 && updated.top < vh + 50) {
            setTargetRect({
              top: updated.top, left: updated.left,
              width: updated.width, height: updated.height,
              bottom: updated.bottom, right: updated.right,
            });
          } else if (measureAttempts < 4) {
            // Not settled yet — scroll again and retry
            el.scrollIntoView({ behavior: 'instant', block: 'center' });
            setTimeout(tryMeasure, 120);
          } else {
            // Give up — use whatever rect we have
            setTargetRect({
              top: updated.top, left: updated.left,
              width: updated.width, height: updated.height,
              bottom: updated.bottom, right: updated.right,
            });
          }
        };
        setTimeout(tryMeasure, 80);
        return;
      }

      // ── Case 4: Element is visible in viewport ───────────────────────────
      clearInterval(pollInterval);
      console.log(`[TourEngine] 📍 Target found after ${attempts} polls:`, { top: Math.round(rect.top), left: Math.round(rect.left) });

      // clickOnFocus: true → programmatically activate the element (e.g. switch a console tab)
      if (step.clickOnFocus) {
        console.log(`[TourEngine] 🖱️ clickOnFocus — clicking "${stepTargetLabel(step)}"`);
        el.click();
      }

      setTargetRect({
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        bottom: rect.bottom, right: rect.right,
      });

      // ── Action polling: if step requires user interaction, wait for it ───
      // IMPORTANT: We enforce a mandatory minimum dwell time of 1200ms before
      // the waitForAction condition is even evaluated. This prevents auto-skipping
      // when the condition happens to already be true the moment the step mounts
      // (e.g. an always-rendered element, or a layout that pre-exists in the DOM).
      if (step.waitForAction) {
        clearInterval(actionPollRef.current);
        const stepStartTime = Date.now();
        const MIN_DWELL_MS = 1200; // user must see the tooltip for at least 1.2s
        const multiTarget = Array.isArray(step.targets) && step.targets.length > 1;
        const actionPollMs = multiTarget ? 150 : 500;

        actionPollRef.current = setInterval(() => {
          try {
            // Re-resolve so spotlight follows alternate targets (e.g. Flow Settings Save after toolbar Save opens the dialog).
            const elNow = resolveTourTargetElement(step);
            if (elNow) {
              const r = elNow.getBoundingClientRect();
              if (r.width > 0 && r.height > 0) {
                const nextRect = {
                  top: r.top, left: r.left, width: r.width, height: r.height,
                  bottom: r.bottom, right: r.right,
                };
                setTargetRect((prev) => {
                  if (prev
                      && Math.abs(prev.top - nextRect.top) < 1
                      && Math.abs(prev.left - nextRect.left) < 1
                      && Math.abs(prev.width - nextRect.width) < 1
                      && Math.abs(prev.height - nextRect.height) < 1) {
                    return prev;
                  }
                  return nextRect;
                });
              }
            }

            // Do not evaluate the condition until the minimum dwell has passed
            if (Date.now() - stepStartTime < MIN_DWELL_MS) return;

            if (step.waitForAction()) {
              clearInterval(actionPollRef.current);
              actionPollRef.current = null;
              console.log(`[TourEngine] ✅ waitForAction resolved for step ${currentIndex} — advancing`);
              setTimeout(() => {
                setCurrentIndex(i => {
                  const next = i + 1;
                  if (next >= (steps?.length ?? 0)) { setRun(false); markSeen(); return i; }
                  return next;
                });
              }, 600);
            }
          } catch (e) {
            // Guard against DOM errors during polling
          }
        }, actionPollMs);
      }
    }, 100);

    return () => {
      clearInterval(pollInterval);
      clearInterval(actionPollRef.current);
      actionPollRef.current = null;
    };
  }, [run, currentIndex, steps]);


  // Position tooltip after targetRect updates
  useEffect(() => {
    if (!run) return;
    rafRef.current = requestAnimationFrame(() => {
      setTooltipPos(computeTooltipPosition(targetRect, tooltipRef));
    });
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [run, targetRect]);

  // Reposition on window resize
  useEffect(() => {
    if (!run) return;
    const handleResize = () => {
      const step = steps?.[currentIndex];
      if (step?.target || (Array.isArray(step?.targets) && step.targets.length)) {
        const rect = getElementRectForStep(step);
        setTargetRect(rect);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [run, currentIndex, steps]);

  // ── Handlers ──
  const handleNext = useCallback(() => {
    if (currentIndex < (steps?.length ?? 0) - 1) {
      console.log(`[TourEngine] ➡️ Next → step ${currentIndex + 1}`);
      setCurrentIndex(i => i + 1);
    } else {
      console.log(`[TourEngine] ✅ Tour finished`);
      setRun(false);
      markSeen();
    }
  }, [currentIndex, steps, setRun, markSeen]);

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      console.log(`[TourEngine] ⬅️ Back → step ${currentIndex - 1}`);
      setCurrentIndex(i => i - 1);
    }
  }, [currentIndex]);

  const handleSkip = useCallback(() => {
    console.log(`[TourEngine] ⏭️ Skipped at step ${currentIndex}`);
    setRun(false);
    markSeen();
  }, [currentIndex, setRun, markSeen]);

  // ── Render ──
  if (!run || !steps?.length) return null;

  const step = steps[currentIndex];

  // Determine wrapper positioning
  const isCentered = tooltipPos.centered === true;
  const wrapperStyle = {
    position: 'fixed',
    zIndex: TOOLTIP_Z,
    // Pointer events block: the card itself is interactive, overlay is not clickable on top
    pointerEvents: 'auto',
    ...(isCentered
      ? {
          // True viewport center using fixed + translate
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }
      : {
          top: tooltipPos.top,
          left: tooltipPos.left,
          transition: 'top 280ms ease, left 280ms ease',
        }
    ),
  };

  return (
    <>
      <SpotlightOverlay targetRect={targetRect} step={step} />
      <div style={wrapperStyle}>
        <TooltipCard
          ref={tooltipRef}
          step={step}
          index={currentIndex}
          total={steps.length}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={handleSkip}
        />
      </div>
    </>
  );
};
